import { tryAuthorizeProject } from '@ff-os/pdp-shared-redux/apis/authorization/tryAuthorizeProject';
import { uploadPrivateAsset } from '@ff-os/pdp-shared-redux/apis/private-assets/uploadPrivateAsset';
import { blobUrlStore } from '@ff-os/rendering-react/asset-loading/blobUrlStore';
import { isSupportedByBrowser } from '@ff-os/shared-react/dom/images/image-browser-support';
import { normalizeImage } from '@ff-os/shared-react/dom/images/normalize-image';
import { PrivateAssetUploadAction } from '@ff-os/shared-utils/types/private-assets/PrivateAssetUploadAction';
import { actions as groupActions } from '@ff-scene-plugins/photo-prints-redux/slices/groupsSlice';
import { PhotoPrintsPdpDispatch, PhotoPrintsPdpThunk } from '@ff-scene-plugins/photo-prints-redux/store';
import { PhotoPrintsUploadContext } from '@ff-scene-plugins/photo-prints-shared/PhotoPrintsUploadContext';
import cuid from 'cuid';
import { detectFacesIn } from '../helpers/detectFacesIn';
import { incrementWithScheduledReset } from './withScheduledReset';

export const uploadImages = (images: File[], groupId: string): PhotoPrintsPdpThunk => {
    return async (dispatch, getState) => {

        dispatch(groupActions.queuePhotos({ groupId, amount: images.length }));

        await tryAuthorizeProject({ dispatch, getState });

        const step = async (index: number) => {

            const nextStep = () => {
                if (index + 1 < images.length) {
                    setTimeout(() => step(index + 1), 0); // let browser repaint
                }
            };

            const image = images[index];
            await uploadImage(image, groupId, dispatch);

            nextStep();
        };

        await step(0);
    };
};

const uploadImage = async (image: File, groupId: string, dispatch: PhotoPrintsPdpDispatch) => {

    const request: PrivateAssetUploadAction<PhotoPrintsUploadContext> = {
        upload: { type: 'Photo', file: image },
        context: {
            id: cuid(),
            assetId: cuid(),
            groupId: groupId,
            processed: false,
            fileName: image.name,
            detections: []
        }
    };

    parseAndUpload(image, request, dispatch)
        .then(() => dispatch(incrementWithScheduledReset(groupId, 'successfulUploadsCount')))
        .catch(() => dispatch(incrementWithScheduledReset(groupId, 'failedUploadsCount')));
};

const parseAndUpload = async (
    image: File,
    request: PrivateAssetUploadAction<PhotoPrintsUploadContext>,
    dispatch: PhotoPrintsPdpDispatch
) => {

    if (isSupportedByBrowser(image)) {

        const { url, width, height } = await normalizeImage(image);

        const detections = await detectFacesIn(url, width, height);

        const photoUpload: PrivateAssetUploadAction<PhotoPrintsUploadContext> = {
            ...request,
            context: {
                ...request.context,
                processed: true,
                fileName: image.name,
                localUrl: url,
                imageWidth: width,
                imageHeight: height,
                autoFaceCenter: true,
                detections: detections
            }
        };

        blobUrlStore.track(url);
        return dispatch(uploadPrivateAsset(photoUpload)).unwrap();
    } else {
        return dispatch(uploadPrivateAsset(request)).unwrap();
    }
};